import { Drawer } from 'components/Drawer'
import { useEffect, useState } from 'react'
import { Heading } from 'blaze-ui/Heading/Heading'
import { observer } from 'mobx-react-lite'
import { Text } from 'blaze-ui/Text/Text'
import Styles from './IntegrationsDrawer.module.scss'
import { ChannelIcon } from 'components/ChannelIcon/ChannelIcon'
import { ChannelType } from 'components/CampaignCreationModal/Types'
import { ChevronRightSmall } from 'blaze-ui/icons/24'

import { ExternalIntegration, ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { integrationToName } from 'components/IntegrationRequiredModal/IntegrationRequiredModal'
import { ExternalIntegrationItem } from 'components/ExternalIntegrationSelect/ExternalIntegrationItem'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { useCreateOrUpdateScheduledPost } from 'components/ArticlePostPreview/hooks'
import { AlmanacFileSummary } from 'types'
import { Check2, Google } from 'blaze-ui/icons'
import { ZapierIntegration } from 'components/IntegrationsModal/components/ZapierIntegration/ZapierIntegration'
import { ZapierLogo } from 'almanac-ui/icons/24'
import { Service } from './Service'
import { SocialChannelType, platformKeyByChannel } from 'common/ShareSheetV2/Screens/Default/Default'
import WordPressLogo from 'almanac-ui/icons/24/WordPressLogo'
import { useRootStore } from 'stores/RootStore'
import { Counter } from 'blaze-ui/Counter/Counter'
import { Integration } from './Integration'
import { DrawerProps } from 'components/Drawer/Root'
import XInitialization from 'components/IntegrationInitialization/XInitialization/XInitialization'
import {
  FacebookInitialization,
  GoogleInitialization,
  InstagramInitialization,
  LinkedinInitialization,
} from 'components/IntegrationInitialization'
import { WordPressInitializationContent } from 'components/IntegrationInitialization/WordPressInitialization/WordPressInitialization'
import cx from 'classnames'
import { useExternalIntegrations } from 'components/ExportModal/ExternalIntegrations/ExternalIntegrations'
import { ButtonLink } from 'blaze-ui/Button/ButtonLink'
import { PlatformIcon } from 'components/ArticlePostPreview/PostPreviewBody/IntegrationsDrawer/PlatformIcon'
import { ProviderToName } from './constants'
import { IntegrationHelpButton } from 'components/IntegrationHelpButton/IntegrationHelpButton'

export interface IntegrationsDrawerProps extends DrawerProps {
  integration?: ExternalIntegration
  provider?: ExternalIntegrationEnum
  almanacFile?: AlmanacFileSummary | Omit<AlmanacFileSummary, 'ancestors'>
}

export const IntegrationsDrawer = observer(
  ({ provider, almanacFile, integration, ...props }: IntegrationsDrawerProps) => {
    const integrationsQuery = useExternalIntegrations()
    const [activeIntegration, setActiveIntegration] = useState<ExternalIntegrationEnum | 'zapier' | null>(null)
    const drawersStore = useRootStore().drawersStore
    const [activeProvider, setActiveProvider] = useState<ExternalIntegrationEnum | null>(null)
    const [wordpressStep, setWordpressStep] = useState(1)

    const integrations = integrationsQuery.data?.external_integrations
    const close = () => drawersStore.closeDrawer()

    const { createOrUpdateScheduledPost } = useCreateOrUpdateScheduledPost({
      almanacFile,
      provider,
    })

    const integrationsForChannel = integrations?.filter(
      (integration) => integration.provider === platformKeyByChannel[almanacFile?.channel as SocialChannelType],
    )

    const updateIntegrationIdImmediately = async (externalIntegrationId: number) => {
      try {
        await createOrUpdateScheduledPost({
          externalIntegrationId,
        })
        close()
        showToastMessage('Account changed!', 'success')
      } catch {
        showToastMessage('Failed to change account', 'error')
      }
    }

    const resetDrawer = () => {
      setActiveProvider(null)
      setActiveIntegration(null)
    }

    const handleBack = () => {
      if (activeIntegration === ExternalIntegrationEnum.WordPress && wordpressStep === 2) {
        setWordpressStep(1)
      } else {
        setActiveIntegration(null)
        setActiveProvider(null)
      }
    }
    useEffect(() => {
      if (activeProvider && integrations?.filter((i) => i.provider === activeProvider).length === 0) resetDrawer()
    }, [activeProvider, integrations])

    const activeIntegrationIsFullHeight = [ExternalIntegrationEnum.WordPress, 'zapier'].includes(
      activeIntegration || '',
    )
    const drawerTitle = activeIntegration ? 'Connect your account' : 'Integrations'
    const leftButton =
      activeIntegration || activeProvider ? (
        <Drawer.Action position="left" onPress={handleBack}>
          Back
        </Drawer.Action>
      ) : (
        <div />
      )
    let rightButton
    if (activeIntegration === 'zapier') {
      rightButton = (
        <div className={Styles.zapierLink}>
          <ButtonLink to="https://zapier.com/app/connections" target="_blank" variant="ghost" size="lg">
            Open Zapier
          </ButtonLink>
        </div>
      )
    } else {
      rightButton = <Drawer.Close position="right" />
    }

    const helpButton = activeIntegration ? (
      <IntegrationHelpButton
        size="sm"
        integration={activeIntegration === 'zapier' ? 'Zapier' : integrationToName[activeIntegration]}
      />
    ) : null

    return (
      <Drawer.Root {...props} onClose={() => setActiveIntegration(null)}>
        <Drawer.Portal>
          <Drawer.Content fullHeight={activeIntegrationIsFullHeight || !!activeProvider}>
            <Drawer.InnerContent isSnappedTop={activeIntegrationIsFullHeight || !!activeProvider}>
              <Drawer.Top>
                {leftButton}
                <Drawer.Title asChild>
                  <Heading level={5}>
                    {activeProvider ? (
                      <div className={Styles.providerTitle}>
                        <PlatformIcon provider={activeProvider} size={20} />
                        {ProviderToName[activeProvider]} Accounts
                      </div>
                    ) : (
                      <>
                        {activeIntegration !== 'zapier' && drawerTitle}
                        {activeIntegration === 'zapier' && 'Zapier'}
                      </>
                    )}
                  </Heading>
                </Drawer.Title>
                <div className={Styles.rightSection}>
                  {helpButton}
                  {rightButton}
                </div>
              </Drawer.Top>
              <Drawer.Container>
                <div className={cx(Styles.container, Styles[activeIntegration || ''])}>
                  {!activeIntegration && !activeProvider && (
                    <>
                      {integrationsForChannel?.length === 0 ? (
                        <>
                          {integrations && integrations.length > 0 && (
                            <div className={Styles.section} key={provider}>
                              <Text variant="placeholder" color="var(--dark-80)">
                                Connected accounts
                              </Text>
                              {Object.values(ExternalIntegrationEnum).map((provider) => (
                                <>
                                  {integrations.filter((i) => i.provider === provider).length > 0 && (
                                    <div className={Styles.services}>
                                      <Service
                                        onClick={() => setActiveProvider(provider)}
                                        icon={<PlatformIcon provider={provider} size={20} />}
                                        label={ProviderToName[provider]}
                                        rightElement={
                                          <Counter count={integrations.filter((i) => i.provider === provider).length} />
                                        }
                                      />
                                    </div>
                                  )}
                                </>
                              ))}
                            </div>
                          )}
                        </>
                      ) : (
                        <div className={Styles.section}>
                          {integrationsForChannel && integrationsForChannel.length > 0 && (
                            <Text variant="placeholder" color="var(--dark-80)">
                              Connected {provider && integrationToName[provider]} accounts
                            </Text>
                          )}
                          <div className={Styles.services}>
                            {integrationsForChannel?.map((externalIntegration) => (
                              <div
                                className={Styles.service}
                                onClick={() => updateIntegrationIdImmediately(externalIntegration.id)}
                                key={externalIntegration.id}
                              >
                                <div className={Styles.serviceChannel}>
                                  <ExternalIntegrationItem externalIntegration={externalIntegration} />
                                </div>
                                {integration?.id === externalIntegration.id ? (
                                  <Check2 color="var(--selected-stroke)" />
                                ) : (
                                  <ChevronRightSmall color="var(--dark-40)" />
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      <div className={Styles.section}>
                        <Text variant="placeholder" color="var(--dark-80)">
                          Connect with {integrations && integrations.length > 0 ? 'more' : ''} services
                        </Text>
                        <div className={Styles.services}>
                          <Service
                            onClick={() => setActiveIntegration(ExternalIntegrationEnum.Instagram)}
                            icon={<ChannelIcon channel={ChannelType.InstagramPosts} />}
                            label="Instagram"
                          />
                          <Service
                            onClick={() => setActiveIntegration(ExternalIntegrationEnum.LinkedIn)}
                            icon={<ChannelIcon channel={ChannelType.LinkedInPosts} />}
                            label="LinkedIn"
                          />
                          <Service
                            onClick={() => setActiveIntegration(ExternalIntegrationEnum.X)}
                            icon={<ChannelIcon channel={ChannelType.Tweets} />}
                            label="X"
                          />
                          <Service
                            onClick={() => setActiveIntegration(ExternalIntegrationEnum.Facebook)}
                            icon={<ChannelIcon channel={ChannelType.FacebookPost} />}
                            label="Facebook"
                          />
                          <Service
                            onClick={() => setActiveIntegration(ExternalIntegrationEnum.WordPress)}
                            icon={<WordPressLogo size={20} />}
                            label="Wordpress"
                          />
                          <Service
                            onClick={() => setActiveIntegration(ExternalIntegrationEnum.Google)}
                            icon={<Google size={20} />}
                            label="Google"
                          />
                        </div>

                        <div className={Styles.services}>
                          <Service
                            onClick={() => setActiveIntegration('zapier')}
                            icon={<ZapierLogo size={20} />}
                            label="Zapier"
                          />
                        </div>
                      </div>
                    </>
                  )}
                  {activeProvider && (
                    <div className={Styles.section}>
                      <Text variant="placeholder" color="var(--dark-80)">
                        Connected {ProviderToName[activeProvider]} Accounts
                      </Text>
                      <div className={Styles.serviceCards}>
                        {integrations
                          ?.filter((i) => i.provider === activeProvider)
                          ?.map((externalIntegration, index) => (
                            <Integration key={index} integration={externalIntegration} />
                          ))}
                      </div>
                    </div>
                  )}
                  {activeIntegration === 'zapier' && <ZapierIntegration inDrawer />}
                  {activeIntegration === ExternalIntegrationEnum.WordPress && (
                    <WordPressInitializationContent
                      step={wordpressStep}
                      setStep={setWordpressStep}
                      onSubmit={resetDrawer}
                    />
                  )}
                  {activeIntegration === ExternalIntegrationEnum.X && <XInitialization useDrawer />}
                  {activeIntegration === ExternalIntegrationEnum.Facebook && <FacebookInitialization useDrawer />}
                  {activeIntegration === ExternalIntegrationEnum.Instagram && <InstagramInitialization useDrawer />}
                  {activeIntegration === ExternalIntegrationEnum.LinkedIn && <LinkedinInitialization useDrawer />}
                  {activeIntegration === ExternalIntegrationEnum.Google && <GoogleInitialization useDrawer />}
                </div>
              </Drawer.Container>
            </Drawer.InnerContent>
          </Drawer.Content>
          <Drawer.Overlay />
        </Drawer.Portal>
      </Drawer.Root>
    )
  },
)
